import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import OurStory from './components/OurStory';
import MissionDrawer from './components/MissionDrawer';
import Features from './components/Features';
import Academy from './components/Academy/Academy';
import Footer from './components/Footer';
import LoginPage from './components/LoginPage';
import AccountPage from './components/AccountPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import CookiePolicy from './components/CookiePolicy';
import TermsConditions from './components/TermsConditions';
import CookieConsentBanner from './components/CookieConsentBanner';
import AcademyMockup from './components/AcademyMockup';
import { getStoredSession, getUser, logout, hasRecoveryParamsInUrl } from './lib/supabaseAuth';
import { computeMastersPathFromEloPoints, fetchMyProfile } from './lib/supabaseProfile';
import { hasAtLeastOneCompletedLesson } from './lib/supabaseProgress';
import './App.css';
import './academy.css';

function App() {
  const [language, setLanguage] = useState('ro');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentView, setCurrentView] = useState('home');
  const [returnView, setReturnView] = useState('home');
  const [accountInitialTab, setAccountInitialTab] = useState('overview');
  const [featureNotice, setFeatureNotice] = useState('');
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [hasCompletedLesson, setHasCompletedLesson] = useState(false);
  const [academyInitialArea, setAcademyInitialArea] = useState('academy');
  const [academyInitialMindhubArticleSlug, setAcademyInitialMindhubArticleSlug] = useState('');

  useEffect(() => {
    const savedLang = localStorage.getItem('chessmindset-lang') || 'ro';
    setLanguage(savedLang);
  }, []);

  useEffect(() => {
    const session = getStoredSession();
    if (session?.access_token) {
      setUser(getUser());
    }
  }, []);

  useEffect(() => {
    let alive = true;
    async function loadProfile() {
      if (!user?.id) {
        setProfile(null);
        return;
      }
      try {
        const p = await fetchMyProfile();
        if (!alive) return;
        if (p) {
          setProfile(p);
          return;
        }
        // If profile row is temporarily missing/null, keep previous profile for this user when available.
        setProfile((prev) => {
          if (prev?.id === user.id) return prev;
          const mp = computeMastersPathFromEloPoints(0);
          return {
            id: user.id,
            nickname: String(user?.user_metadata?.nickname || user?.user_metadata?.display_name || '').trim(),
            eloPoints: 0,
            computedLevel: 1,
            computedRankKey: 'explorer',
            mastersPath: mp
          };
        });
      } catch {
        if (!alive) return;
        // Do not overwrite a valid loaded profile with a transient 0 fallback.
        setProfile((prev) => {
          if (prev?.id === user.id) return prev;
          const mp = computeMastersPathFromEloPoints(0);
          return {
            id: user.id,
            nickname: String(user?.user_metadata?.nickname || user?.user_metadata?.display_name || '').trim(),
            eloPoints: 0,
            computedLevel: 1,
            computedRankKey: 'explorer',
            mastersPath: mp
          };
        });
        // Retry once shortly after network/session recovers.
        window.setTimeout(() => {
          if (!alive) return;
          loadProfile();
        }, 900);
      }
    }
    loadProfile();
    return () => {
      alive = false;
    };
  // user_metadata can be updated during signup/login; include it so nickname fallback doesn't get stale.
  }, [user?.id, user?.user_metadata?.nickname, user?.user_metadata?.display_name]);

  useEffect(() => {
    let alive = true;
    async function loadHasCompletedLesson() {
      if (!user?.id) {
        setHasCompletedLesson(false);
        return;
      }
      const value = await hasAtLeastOneCompletedLesson();
      if (!alive) return;
      setHasCompletedLesson(Boolean(value));
    }
    loadHasCompletedLesson();
    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    // Allow direct access to "pages" via hash URLs (e.g. /#/login).
    const syncFromHash = () => {
      if (hasRecoveryParamsInUrl()) {
        setCurrentView('login');
        return;
      }
      const fullHash = String(window.location.hash || '').toLowerCase();
      const hash = fullHash.split('?')[0];
      if (hash === '#/login') setCurrentView('login');
      else if (hash === '#/academy') setCurrentView('academy');
      else if (hash === '#/mindhub') {
        setAcademyInitialArea('mindhub');
        setAcademyInitialMindhubArticleSlug('');
        setCurrentView('academy');
      } else if (hash === '#/playground') {
        setAcademyInitialArea('playground');
        setAcademyInitialMindhubArticleSlug('');
        setCurrentView('academy');
      } else if (hash.startsWith('#/mindhub/')) {
        const slug = decodeURIComponent(hash.replace('#/mindhub/', '').trim());
        setAcademyInitialArea('mindhub');
        setAcademyInitialMindhubArticleSlug(slug);
        setCurrentView('academy');
      }
      else if (hash === '#/academy-mock') setCurrentView('academy-mock');
      else if (hash === '#/account') setCurrentView('account');
      else if (hash === '#/billing') {
        setAccountInitialTab('billing');
        setCurrentView('account');
      }
      else if (hash === '#/privacy') setCurrentView('privacy');
      else if (hash === '#/cookies') setCurrentView('cookies');
      else if (hash === '#/terms') setCurrentView('terms');
      else setCurrentView('home');
    };
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, []);

  useEffect(() => {
    // Keep URL in sync with current view.
    // Important: when Supabase recovery tokens are in URL hash/search,
    // do not overwrite the URL before LoginPage consumes them.
    if (hasRecoveryParamsInUrl()) {
      return;
    }
    const desired =
      currentView === 'login'
        ? '#/login'
        : currentView === 'academy'
          ? (
            academyInitialArea === 'mindhub'
              ? (academyInitialMindhubArticleSlug
                ? `#/mindhub/${encodeURIComponent(academyInitialMindhubArticleSlug)}`
                : '#/mindhub')
              : academyInitialArea === 'playground'
                ? '#/playground'
              : '#/academy'
          )
          : currentView === 'academy-mock'
            ? '#/academy-mock'
          : currentView === 'account'
            ? (accountInitialTab === 'billing' ? '#/billing' : '#/account')
          : currentView === 'privacy'
            ? '#/privacy'
            : currentView === 'cookies'
              ? '#/cookies'
              : currentView === 'terms'
                ? '#/terms'
            : '#/';
    if (window.location.hash !== desired) {
      window.location.hash = desired;
    }
  }, [accountInitialTab, currentView, academyInitialArea, academyInitialMindhubArticleSlug]);

  useEffect(() => {
    // Always reset scroll when switching SPA views so each screen opens from the top.
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
  }, [currentView]);

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    localStorage.setItem('chessmindset-lang', lang);
  };

  const openDrawer = () => {
    setDrawerOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    document.body.style.overflow = '';
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') closeDrawer();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    if (!featureNotice) return undefined;
    const timeoutId = window.setTimeout(() => setFeatureNotice(''), 3000);
    return () => window.clearTimeout(timeoutId);
  }, [featureNotice]);

  const handleFeatureClick = (featureIndex) => {
    // UX request: any homepage feature card should open Academy
    // exactly like the primary "Explorează academia" CTA.
    const targetArea = 'academy';
    setFeatureNotice('');
    setAcademyInitialArea(targetArea);
    setAcademyInitialMindhubArticleSlug('');
    setCurrentView('academy');
  };

  const handleOpenAcademy = (area = 'academy') => {
    closeDrawer();
    setAcademyInitialArea(area || 'academy');
    setAcademyInitialMindhubArticleSlug('');
    setCurrentView('academy');
  };

  const navigateToLogin = (fromView) => {
    setReturnView(fromView || currentView || 'home');
    closeDrawer();
    setCurrentView('login');
  };

  const navigateHome = () => {
    closeDrawer();
    setCurrentView('home');
  };

  return (
    <div className="App">
      <Header
        language={language}
        onLanguageChange={handleLanguageChange}
        user={user}
        profile={profile}
        onRequestLogin={() => navigateToLogin(currentView)}
        onOpenAccount={(tab = 'overview') => {
          setAccountInitialTab(tab);
          setReturnView(currentView || 'home');
          setCurrentView('account');
        }}
        onNavigateHome={navigateHome}
        onProfileUpdated={async () => {
          try {
            const p = await fetchMyProfile();
            setProfile(p);
          } catch {
            // ignore
          }
        }}
      />
      
      {currentView === 'home' ? (
        <>
          <Hero
            language={language}
            onOpenDrawer={openDrawer}
            onOpenAcademy={handleOpenAcademy}
            emphasizeContinuePathCta={Boolean(user?.id && hasCompletedLesson)}
          />
          <OurStory
            language={language}
            onOpenAcademy={handleOpenAcademy}
          />
          <MissionDrawer
            language={language}
            isOpen={drawerOpen}
            onClose={closeDrawer}
            onOpenAcademy={handleOpenAcademy}
          />
          <Features language={language} onFeatureClick={handleFeatureClick} />
        </>
      ) : currentView === 'academy' ? (
        <Academy
          language={language}
          user={user}
          profile={profile}
          initialArea={academyInitialArea}
          initialMindhubArticleSlug={academyInitialMindhubArticleSlug}
          onRequestLogin={() => navigateToLogin('academy')}
          onAreaChange={(nextArea) => {
            setAcademyInitialArea(nextArea || 'academy');
            if (nextArea !== 'mindhub') setAcademyInitialMindhubArticleSlug('');
          }}
          onMindhubArticleChange={(nextSlug) => {
            const safeSlug = nextSlug || '';
            setAcademyInitialMindhubArticleSlug(safeSlug);
            if (safeSlug) {
              setAcademyInitialArea('mindhub');
            }
          }}
          onOpenAccount={(tab = 'overview') => {
            setAccountInitialTab(tab);
            setReturnView('academy');
            setCurrentView('account');
          }}
          onProfileUpdated={async () => {
            try {
              const p = await fetchMyProfile();
              setProfile(p);
            } catch {
              // ignore
            }
          }}
        />
      ) : currentView === 'academy-mock' ? (
        <AcademyMockup
          language={language}
          onBack={() => setCurrentView('home')}
        />
      ) : currentView === 'account' ? (
        <AccountPage
          language={language}
          user={user}
          profile={profile}
          initialTab={accountInitialTab}
          onBack={() => setCurrentView(returnView || 'home')}
          onRequestLogin={() => navigateToLogin('account')}
          onLogout={() => {
            logout();
            setUser(null);
            setProfile(null);
            setReturnView('home');
            setCurrentView('home');
          }}
          onProfileUpdated={async () => {
            try {
              const p = await fetchMyProfile();
              setProfile(p);
            } catch {
              // ignore
            }
          }}
          onLanguageChange={handleLanguageChange}
        />
      ) : currentView === 'privacy' ? (
        <PrivacyPolicy
          language={language}
          onBack={() => setCurrentView(returnView || 'home')}
        />
      ) : currentView === 'cookies' ? (
        <CookiePolicy
          language={language}
          onBack={() => setCurrentView(returnView || 'home')}
        />
      ) : currentView === 'terms' ? (
        <TermsConditions
          language={language}
          onBack={() => setCurrentView(returnView || 'home')}
        />
      ) : (
        <LoginPage
          language={language}
          onLanguageChange={handleLanguageChange}
          user={user}
          profile={profile}
          onBack={() => setCurrentView(returnView || 'home')}
          onGoHome={navigateHome}
          onLogin={(session) => {
            setUser(session ? getUser() : null);
            // profile effect will re-load on user change
            setCurrentView(returnView || 'home');
          }}
          onProfileUpdated={async () => {
            // Refresh profile after nickname save.
            try {
              const p = await fetchMyProfile();
              setProfile(p);
            } catch {
              // ignore
            }
          }}
        />
      )}

      {featureNotice && <div className="feature-notice">{featureNotice}</div>}

      <Footer
        language={language}
        onOpenPrivacy={() => {
          setReturnView(currentView || 'home');
          setCurrentView('privacy');
        }}
        onOpenCookies={() => {
          setReturnView(currentView || 'home');
          setCurrentView('cookies');
        }}
        onOpenTerms={() => {
          setReturnView(currentView || 'home');
          setCurrentView('terms');
        }}
      />
      <CookieConsentBanner
        language={language}
        onOpenPrivacy={() => {
          setReturnView(currentView || 'home');
          setCurrentView('privacy');
        }}
        onOpenCookies={() => {
          setReturnView(currentView || 'home');
          setCurrentView('cookies');
        }}
        onOpenTerms={() => {
          setReturnView(currentView || 'home');
          setCurrentView('terms');
        }}
      />
    </div>
  );
}

export default App;
